import React, { useState } from 'react'

import { useDeleteManualRentalHistory } from 'hooks/useRentalHistoryQueries'

import homeIcon from 'app/assets/icons/summary_icons/home.svg'
import {
  Alert,
  Box,
  Button,
  Flex,
  TextSpan,
} from 'app/components/design-system-components'
import { AlertInfo } from 'app/components/design-system-components/AlertInfo'
import Modal from 'app/components/design-system-components/Modal'
import { ContainerHeader } from 'app/components/display/text/standard_text/standard-headings/component'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import AddAddressHistoryModal from 'app/match/apply/application_summary_before_submit/components/address_history_container/AddAddressHistoryModal'
import SummaryStaticHistoryBox from 'app/match/apply/application_summary_before_submit/shared_components/history_box/component'
import { AddressHistoryDetailsComponent } from 'app/match/rental_reference/display/renter_address_history_detailed/component'
import Checkbox from 'app/shared_components/checkbox'
import { getCurrentOrMostRecentRentalLedgerRequiredHistory } from 'app/sm/apply/rental-ledger-required-policy.helpers'
import { noteActionType, urlTo } from 'app/sm/helpers'
import { QLD_ADDRESS_HISTORY_HEADER_ALERT } from 'app/utils/text/helpers'

const SUBMIT_LEDGER_WARNING_MESSAGE = 'A rental ledger is required.'

const hasUploadedRentalLedger = (addressHistory) => {
  const ledgers = addressHistory?.ledger || []
  return ledgers.some((ledger) => ledger?.addedByRenter)
}

const AddressHistoryComponent = ({
  componentClassName = '',
  rentalHistory = [],
  optOutRentalHistory = false,
  isAddressHistoryEditable = true,
  onEditClick,
  enableNote = false,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  displayedApplicant,
  section,
  ballotsEnabled = false,
  renterName = '',
  excludeRequestReasonForMoving = false,
  isQLDForm22 = false,
  isRentalLedgerRequiredByAgencyPolicy = false,
  showSubmitLedgerError = false,
  submitLedgerErrorMessage = '',
  missingRentalHistoryGUIDs = [],
  onFixMissingRentalLedger = undefined,
}) => {
  const rentalHistoryList = Array.isArray(rentalHistory) ? rentalHistory : []
  const currentOrMostRecentRentalHistory =
    getCurrentOrMostRecentRentalLedgerRequiredHistory(rentalHistoryList)
  const policyMissingRentalHistoryGUIDs =
    isRentalLedgerRequiredByAgencyPolicy &&
    currentOrMostRecentRentalHistory &&
    !hasUploadedRentalLedger(currentOrMostRecentRentalHistory) &&
    currentOrMostRecentRentalHistory.guidID
      ? [currentOrMostRecentRentalHistory.guidID]
      : []
  const hasAnyMissingRentalLedger =
    isRentalLedgerRequiredByAgencyPolicy &&
    policyMissingRentalHistoryGUIDs.length > 0
  const hasSubmitLedgerError =
    showSubmitLedgerError && !!submitLedgerErrorMessage
  let highlightedRentalHistoryGUIDs = []
  if (hasSubmitLedgerError && missingRentalHistoryGUIDs.length > 0) {
    highlightedRentalHistoryGUIDs = missingRentalHistoryGUIDs
  } else if (hasAnyMissingRentalLedger) {
    highlightedRentalHistoryGUIDs = policyMissingRentalHistoryGUIDs
  }
  const policyMissingRentalHistoryGUIDSet = new Set(
    policyMissingRentalHistoryGUIDs,
  )
  const highlightedRentalHistoryGUIDSet = new Set(highlightedRentalHistoryGUIDs)

  return (
    <div className={`container-component ${componentClassName}`}>
      <ContainerHeader
        icon={homeIcon}
        text="Address History"
        isEditable={isAddressHistoryEditable}
        onEditClick={() =>
          onEditClick(
            `${urlTo('bondCoverRentalHistory')}?from=rentersummary${
              ballotsEnabled && `&ballot=true`
            }`,
          )
        }
        enableNote={enableNote}
        onNewNoteUpdated={onNewNoteUpdated}
        newNotesContent={newNotesContent}
        onNoteIconClicked={onNoteIconClicked}
        onNewNoteAdded={onNewNoteAdded}
        onNoteCrossClicked={onNoteCrossClicked}
        interpreteNoteResult={interpreteNoteResult}
        enableNotesButton={enableNotesButton}
        displayedApplicant={displayedApplicant}
        section={section}
      />

      {isQLDForm22 && (
        <AlertInfo mt={8} mb={8} message={QLD_ADDRESS_HISTORY_HEADER_ALERT} />
      )}
      <div>
        {rentalHistoryList.length > 0 &&
          rentalHistoryList.map((addressInfo) => {
            const isLedgerWarningHighlighted =
              highlightedRentalHistoryGUIDSet.has(addressInfo.guidID)
            return (
              <React.Fragment key={addressInfo.guidID}>
                <SummaryStaticHistoryBox
                  H={addressInfo}
                  hasGreyBackground
                  isSubmitErrorHighlighted={isLedgerWarningHighlighted}
                  showMissingLedgerWarning={isLedgerWarningHighlighted}
                  missingLedgerWarningMessage={SUBMIT_LEDGER_WARNING_MESSAGE}
                  onFixMissingRentalLedger={
                    isLedgerWarningHighlighted
                      ? onFixMissingRentalLedger
                      : undefined
                  }
                  isRentalLedgerRequiredForAddressByPolicy={policyMissingRentalHistoryGUIDSet.has(
                    addressInfo.guidID,
                  )}
                  isRentalLedgerRequiredByAgencyPolicy={
                    isRentalLedgerRequiredByAgencyPolicy
                  }
                  isQLDForm22={isQLDForm22}
                />
              </React.Fragment>
            )
          })}
        {rentalHistoryList.length === 0 && !optOutRentalHistory && (
          <div>Not provided</div>
        )}
        {rentalHistoryList.length === 0 && optOutRentalHistory && (
          <div>Does not have residences</div>
        )}
        {rentalHistoryList.length !== 0 && optOutRentalHistory && (
          <div className="disabled-link">
            <Checkbox
              label="No further address history"
              value={optOutRentalHistory}
              readOnly={true}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export const ManagerAddressHistoryComponent = ({
  componentClassName = '',
  rentalHistory = [],
  optOutRentalHistory = false,
  hasRentalBondsAccount = false,
  isAddressHistoryEditable = true,
  onEditClick,
  enableNote = false,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  displayedApplicant,
  section,
  handleSubmitRentalReviewRequest,
  versionsRatingCategories,
  selectedApplicantGUID,
  applicationId,
  onRemoveRentalReference,
  agencyGUID,
  id = '',
  toggleRequestInfoModal = undefined,
  ballotsEnabled = false,
  toggleDocumentsViewer,
  renterName = '',
  excludeRequestReasonForMoving = false,
  excludeTenure = false,
  excludeReferenceRequests = false,
  smartDocsAnalysis,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
  isQLDForm22 = false,
  isSAPrescribedForm = false,
  isManagerAdded = false,
  isRentalLedgerRequiredByAgencyPolicy = false,
  match,
  manualRentalHistory = [],
  onManualRentalHistoryUpdate, // New callback for updating manual rental history state
  applicants = [], // New prop for auto-fill functionality from application.applicants
  showRentalBondsAccount = true,
  showAddressHistoryRent = true,
}) => {
  const [addAddressHistoryModalOpen, setAddAddressHistoryModalOpen] =
    useState(false)
  const [selectedManualRentalHistory, setSelectedManualRentalHistory] =
    useState()
  const [isEditMode, setIsEditMode] = useState(false)
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false)
  const [addressToRemove, setAddressToRemove] = useState(null)

  // Hook for deleting manual rental history
  const deleteManualRentalHistoryMutation = useDeleteManualRentalHistory()

  const rentalBondsAccountNote = (
    <>
      <b>
        {displayedApplicant.firstName} {displayedApplicant.lastName}{' '}
      </b>
      {hasRentalBondsAccount && (
        <>
          has a <b> rental bond account</b>
        </>
      )}{' '}
      {!hasRentalBondsAccount && (
        <>
          doesn't have a<b> rental bond account</b>
        </>
      )}
    </>
  )

  const getRentalReferenceFilteredLastNotes = (addressInfo) => {
    const noteResult = JSON.parse(JSON.stringify(interpreteNoteResult || {}))
    const allNotes = JSON.parse(
      JSON.stringify(noteResult.addressHistory?.categorizedAllNotes || {}),
    )
    let filteredCategorizedLastNotes = {}

    if (!Object.keys(noteResult).length) {
      return
    }

    // - split notes over multiple rental histories.
    // - find the latest notes for each action type with targetGUID = addressInfo.guidID
    // - or the latest with empty targetGUID to fall back the old record
    const getLatestTypeRecord = (type) => {
      let typeNotes = allNotes[type]?.filter((note) => {
        return note.targetGUID === addressInfo.guidID
      })

      if (!typeNotes.length) {
        typeNotes = allNotes[type]?.filter((note) => {
          return note.targetGUID.length === 0
        })
      }

      if (!typeNotes.length) return undefined

      return typeNotes.reduce(
        (n1, n2) =>
          new Date(n1?.updatedAt) > new Date(n2?.updatedAt) ? n1 : n2,
        undefined,
      )
    }

    if (allNotes) {
      filteredCategorizedLastNotes[noteActionType.approve] =
        getLatestTypeRecord(noteActionType.approve)
      filteredCategorizedLastNotes[noteActionType.decline] =
        getLatestTypeRecord(noteActionType.decline)
      filteredCategorizedLastNotes[noteActionType.comment] =
        getLatestTypeRecord(noteActionType.comment)

      // find the latest note among variant action types
      const latestTargetNote = Object.values(filteredCategorizedLastNotes)
        .filter((n) => !!n)
        .reduce(
          (n1, n2) =>
            new Date(n1?.updatedAt) > new Date(n2?.updatedAt) ? n1 : n2,
          undefined,
        )

      noteResult.addressHistory.type = latestTargetNote?.type || 0

      noteResult.addressHistory.categorizedLastNotes =
        filteredCategorizedLastNotes
    }

    return noteResult
  }

  const toggleAddAddressHistory = () => {
    setAddAddressHistoryModalOpen(!addAddressHistoryModalOpen)
  }

  const onEditManualRentalHistory = (updatedHistory) => {
    // Update the manual rental history with the edited entry
    setSelectedManualRentalHistory(updatedHistory)
    setIsEditMode(true)
    toggleAddAddressHistory()
  }

  const onAddManualRentalHistory = () => {
    setSelectedManualRentalHistory(null)
    setIsEditMode(false)
    toggleAddAddressHistory()
  }

  const handleManualRentalHistorySuccess = (data) => {
    // Update the manual rental history array in parent component state
    if (onManualRentalHistoryUpdate) {
      if (isEditMode && selectedManualRentalHistory) {
        // Edit mode: replace existing entry by matching guidID
        onManualRentalHistoryUpdate(
          data,
          'update',
          selectedManualRentalHistory.guidID,
        )
      } else {
        // Add mode: append new entry to array
        onManualRentalHistoryUpdate(data, 'add')
      }
    }

    // Close modal and reset state
    toggleAddAddressHistory()
    setSelectedManualRentalHistory(null)
    setIsEditMode(false)
  }

  // Check if address history is locked (reference has been sent)
  const isAddressHistoryLocked = (addressInfo) => {
    return !!(
      addressInfo?.lastReferenceRequest &&
      addressInfo?.lastReferenceRequest?.requestSender &&
      addressInfo?.lastReferenceRequest?.requestReceiver &&
      addressInfo?.lastReferenceRequest?.requestSentTime
    )
  }

  // Handle remove button click
  const handleRemoveClick = (addressInfo) => {
    setAddressToRemove(addressInfo)
    setShowRemoveConfirmation(true)
  }

  // Handle remove confirmation
  const handleConfirmRemove = async () => {
    if (addressToRemove) {
      try {
        // Use the hook to delete the rental history record
        await deleteManualRentalHistoryMutation.mutateAsync({
          rentalHistoryId: addressToRemove.guidID,
          applicantGuid: displayedApplicant.guidID,
          agencyGuid: agencyGUID,
        })

        // Update the local state after successful deletion
        if (onManualRentalHistoryUpdate) {
          onManualRentalHistoryUpdate(null, 'delete', addressToRemove.guidID)
        }
      } catch (error) {
        // Error handling is handled by the hook (snugNotifier.error)
        console.error('Failed to delete rental history:', error)
      }
    }
    setShowRemoveConfirmation(false)
    setAddressToRemove(null)
  }

  // Handle cancel remove
  const handleCancelRemove = () => {
    setShowRemoveConfirmation(false)
    setAddressToRemove(null)
  }

  const rentalHistoryList = isManagerAdded
    ? manualRentalHistory || []
    : rentalHistory || []

  const addRentalReferenceButton = (
    <Button onClick={onAddManualRentalHistory}>Add reference</Button>
  )

  return (
    <div id={id} className={`container-component ${componentClassName}`}>
      <div className={`container-header p osition-relative`}>
        <img
          className="standard-icon-spacing"
          src={homeIcon}
          alt="Summary Icon"
        />
        <span className={`standard-subheading`}>Address History</span>
      </div>

      {!isQLDForm22 &&
        !ballotsEnabled &&
        !isManagerAdded &&
        showRentalBondsAccount && <Box mt="20px">{rentalBondsAccountNote}</Box>}

      {isManagerAdded && (
        <Box mt={5} mb={7}>
          {rentalHistoryList.length === 0 && (
            <Box mb={5}>{addRentalReferenceButton}</Box>
          )}
          <Alert variant="blueWithBg" showLeftIcon leftIconVariant="info">
            Add address history to send a reference request
          </Alert>
        </Box>
      )}

      <div>
        {rentalHistoryList.length > 0 &&
          rentalHistoryList.map((addressInfo, idx) => {
            const hasRatingCategories =
              versionsRatingCategories &&
              versionsRatingCategories[addressInfo.referenceSetVersion]

            return (
              <Box key={addressInfo.guidID} mt={`${theme.space[4]}px`}>
                <ContainerHeader
                  isEditable={isAddressHistoryEditable && !isManagerAdded}
                  onEditClick={() =>
                    onEditClick(
                      `${urlTo('bondCoverRentalHistory')}?from=rentersummary`,
                    )
                  }
                  enableNote={enableNote}
                  onNewNoteUpdated={onNewNoteUpdated}
                  newNotesContent={newNotesContent}
                  onNoteIconClicked={onNoteIconClicked}
                  onNewNoteAdded={onNewNoteAdded}
                  onNoteCrossClicked={onNoteCrossClicked}
                  interpreteNoteResult={getRentalReferenceFilteredLastNotes(
                    addressInfo,
                  )}
                  enableNotesButton={enableNotesButton}
                  displayedApplicant={displayedApplicant}
                  section={section}
                  toggleRequestInfoModal={toggleRequestInfoModal}
                  targetGUID={addressInfo.guidID}
                  text={idx === 0 && addRentalReferenceButton}
                />

                {/* Edit/Remove Actions for Manual Rental History */}
                {!isAddressHistoryLocked(addressInfo) && isManagerAdded && (
                  <Box mt="24px" mb="-24px">
                    <Flex justifyContent="end" alignItems="center">
                      <Flex alignItems="center" gap="8px">
                        <Button
                          variant="linkBlue"
                          size="large"
                          onClick={(e) => {
                            e.preventDefault()
                            onEditManualRentalHistory(addressInfo)
                          }}
                          p={0}
                          mr={5}
                        >
                          Edit
                        </Button>

                        <Button
                          variant="linkBlue"
                          size="large"
                          onClick={(e) => {
                            e.preventDefault()
                            handleRemoveClick(addressInfo)
                          }}
                          p={0}
                        >
                          Remove
                        </Button>
                      </Flex>
                    </Flex>
                  </Box>
                )}

                {hasRatingCategories ? (
                  <AddressHistoryDetailsComponent
                    addressInfo={addressInfo}
                    key={addressInfo.guidID}
                    handleSubmitRentalReviewRequest={
                      handleSubmitRentalReviewRequest
                    }
                    ratingCategories={
                      versionsRatingCategories[addressInfo.referenceSetVersion]
                    }
                    referenceSetVersion={addressInfo.referenceSetVersion}
                    selectedApplicantGUID={selectedApplicantGUID}
                    applicationId={applicationId}
                    onRemoveRentalReference={onRemoveRentalReference}
                    agencyGUID={agencyGUID}
                    toggleDocumentsViewer={toggleDocumentsViewer}
                    renterName={renterName}
                    excludeRequestReasonForMoving={
                      excludeRequestReasonForMoving
                    }
                    excludeTenure={excludeTenure}
                    excludeReferenceRequests={excludeReferenceRequests}
                    smartDocsAnalysis={smartDocsAnalysis}
                    onClickConfirmOKForAnalysis={onClickConfirmOKForAnalysis}
                    onClickUndoForAnalysis={onClickUndoForAnalysis}
                    isQLDForm22={isQLDForm22}
                    isManagerAdded={isManagerAdded}
                    showAddressHistoryRent={showAddressHistoryRent}
                  />
                ) : (
                  <SummaryStaticHistoryBox
                    H={addressInfo}
                    hasGreyBackground
                    isQLDForm22={isQLDForm22}
                    isSAPrescribedForm={isSAPrescribedForm}
                    excludeTenure={excludeTenure}
                    showAddressHistoryRent={showAddressHistoryRent}
                  />
                )}
              </Box>
            )
          })}

        {addAddressHistoryModalOpen && (
          <AddAddressHistoryModal
            onClose={toggleAddAddressHistory}
            onSave={handleManualRentalHistorySuccess}
            onManualRentalHistoryUpdate={onManualRentalHistoryUpdate}
            match={match}
            applicantGUID={displayedApplicant.guidID}
            agencyGUID={agencyGUID}
            editingRentalHistory={selectedManualRentalHistory}
            isEditMode={isEditMode}
            isQLDForm22={isQLDForm22}
            isRentalLedgerRequiredByAgencyPolicy={
              isRentalLedgerRequiredByAgencyPolicy
            }
            rentalHistoryForPolicy={rentalHistoryList}
            applicants={applicants}
          />
        )}

        {!isManagerAdded && (
          <>
            {rentalHistoryList.length === 0 && !optOutRentalHistory && (
              <div>Not provided</div>
            )}
            {rentalHistoryList.length === 0 && optOutRentalHistory && (
              <div>Does not have residences</div>
            )}
            {rentalHistoryList.length !== 0 &&
              optOutRentalHistory &&
              !isSAPrescribedForm && (
                <div className="disabled-link">
                  <Checkbox
                    label="No further address history"
                    value={optOutRentalHistory}
                    readOnly={true}
                  />
                </div>
              )}
          </>
        )}
      </div>

      {/* Remove Confirmation Modal */}
      {showRemoveConfirmation && addressToRemove && (
        <Modal
          modalHeading="Remove Address History"
          primaryLabel="Remove"
          secondaryLabel="Cancel"
          primaryAction={handleConfirmRemove}
          secondaryAction={handleCancelRemove}
          toggleModal={handleCancelRemove}
          primaryButtonLoading={deleteManualRentalHistoryMutation.isPending}
          modalBody={
            <Box>
              <TextSpan>
                Are you sure you want to remove this address history? This
                action cannot be undone.
              </TextSpan>
              <Box mt="16px">
                <TextSpan fontWeight="bold" fontSize="14px">
                  {addressToRemove?.address?.friendlyName},{' '}
                  {addressToRemove?.address?.suburb},{' '}
                  {addressToRemove?.address?.state},{' '}
                  {addressToRemove?.address?.postcode}
                </TextSpan>
              </Box>
            </Box>
          }
        />
      )}
    </div>
  )
}

export default AddressHistoryComponent
